/**
 * AR / AP settlement read API hooks(REQ-20260522 Phase 3B-Lite)。
 */
import { useQuery } from '@tanstack/react-query';
import type {
  ApiSuccess,
  ApSettlementDetail,
  ApSettlementListResponse,
  ArSettlementDetail,
  ArSettlementListResponse,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

const PAGE_SIZE = 50;

// ─── AR ──────────────────────────────────────────────

export function useCustomerArSettlements(
  customerId: number | undefined,
  page: number,
): ReturnType<typeof useQuery<ArSettlementListResponse>> {
  return useQuery<ArSettlementListResponse>({
    queryKey: ['ar-settlements', 'by-customer', customerId, page],
    enabled: customerId != null && Number.isFinite(customerId),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<ArSettlementListResponse>>(
        `/customers/${customerId}/ar-settlements`,
        { params: { limit: PAGE_SIZE, offset: page * PAGE_SIZE } },
      );
      return res.data.data;
    },
  });
}

/** 取某客戶 AR 沖帳總筆數(給 tab badge 顯示);limit=1 最小 payload。 */
export function useCustomerArSettlementsTotal(
  customerId: number | undefined,
): ReturnType<typeof useQuery<number>> {
  return useQuery<number>({
    queryKey: ['ar-settlements', 'by-customer-total', customerId],
    enabled: customerId != null && Number.isFinite(customerId),
    staleTime: 1000 * 30,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<ArSettlementListResponse>>(
        `/customers/${customerId}/ar-settlements`,
        { params: { limit: 1, offset: 0 } },
      );
      return res.data.data.total;
    },
  });
}

export function useArSettlement(
  id: number | null,
): ReturnType<typeof useQuery<ArSettlementDetail>> {
  return useQuery<ArSettlementDetail>({
    queryKey: ['ar-settlements', 'detail', id],
    enabled: id != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<ArSettlementDetail>>(`/ar-settlements/${id}`);
      return res.data.data;
    },
  });
}

// ─── AP ──────────────────────────────────────────────

export function useSupplierApSettlements(
  supplierId: number | undefined,
  page: number,
): ReturnType<typeof useQuery<ApSettlementListResponse>> {
  return useQuery<ApSettlementListResponse>({
    queryKey: ['ap-settlements', 'by-supplier', supplierId, page],
    enabled: supplierId != null && Number.isFinite(supplierId),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<ApSettlementListResponse>>(
        `/suppliers/${supplierId}/ap-settlements`,
        { params: { limit: PAGE_SIZE, offset: page * PAGE_SIZE } },
      );
      return res.data.data;
    },
  });
}

export function useApSettlement(
  id: number | null,
): ReturnType<typeof useQuery<ApSettlementDetail>> {
  return useQuery<ApSettlementDetail>({
    queryKey: ['ap-settlements', 'detail', id],
    enabled: id != null,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<ApSettlementDetail>>(`/ap-settlements/${id}`);
      return res.data.data;
    },
  });
}

export const SETTLEMENTS_PAGE_SIZE = PAGE_SIZE;
